import { UUID_REGEX } from '@/lib/api';

const TRIGGER_TEMPLATE_RE = /\{\{\s*trigger\./;

/** True when flow steps interpolate trigger.* placeholders (manual runs need matching payload keys). */
export function flowUsesTriggerTemplates(fm: Record<string, unknown>): boolean {
  const steps = fm.steps;
  if (!Array.isArray(steps)) return false;
  try {
    return TRIGGER_TEMPLATE_RE.test(JSON.stringify(steps));
  } catch {
    return false;
  }
}

/** Sample trigger payload for manual Run now on zoom.transcript_ready flows. */
export function buildZoomManualTestPayload(overrides: { topic?: string } = {}): Record<string, unknown> {
  const now = new Date();
  const iso = now.toISOString();
  const topic = overrides.topic?.trim() || 'Manual QA test meeting';
  const plainText =
    'Alex Example: Checking the Zoom notes pipeline.\nJordan Sample: Sounds good - one action item for follow-up.';
  return {
    trigger_type: 'zoom.transcript_ready',
    meetingId: 'manual-test',
    meetingUuid: 'manual-test-uuid',
    topic,
    startTime: iso,
    meetingDate: iso.slice(0, 10),
    hostEmail: 'qa@example.com',
    plainText,
    plainTextLength: plainText.length,
    durationMinutes: 15,
    durationLabel: '15 min',
    timezone: 'America/Los_Angeles',
    shareUrl: '',
    attendees: 'Alex Example, Jordan Sample',
    attendeeCount: 2,
    vttContent: '',
    downloadUrl: '',
    slug: 'manual-qa-test-meeting',
  };
}

export type CollectManualTriggerPayloadResult =
  | { cancelled: true }
  | { cancelled: false; payload: Record<string, unknown> };

/** True when manual Run now needs a synthetic trigger payload before POST /api/flows/:id/trigger. */
export function needsManualTriggerPayload(
  fm?: Record<string, unknown>,
  triggers?: Array<{ id: string }>,
): boolean {
  if (fm && flowUsesTriggerTemplates(fm)) return true;
  if (triggers?.some((t) => t.id === 'document.edited')) return true;
  if (triggers?.some((t) => t.id === 'zoom.transcript_ready')) return true;
  return false;
}

/**
 * For flows wired to document.edited, collect trigger payload for POST /api/flows/:id/trigger.
 * Uses window.prompt (operator QA); saving the doc still fires document.edited with full payload.
 */
export function collectManualTriggerPayloadInteractive(
  fm: Record<string, unknown>,
  defaults: { file_path?: string },
  triggers?: Array<{ id: string }>,
): CollectManualTriggerPayloadResult {
  const triggerList =
    triggers ??
    (Array.isArray(fm.triggers) ? (fm.triggers as Array<{ id: string }>) : []);

  if (triggerList.some((t) => t.id === 'zoom.transcript_ready')) {
    const topicRaw = window.prompt(
      'Optional: meeting topic for this manual Zoom flow test (Cancel uses a default).',
      'Manual QA test meeting',
    );
    if (topicRaw === null) return { cancelled: true };
    return {
      cancelled: false,
      payload: buildZoomManualTestPayload({ topic: topicRaw || undefined }),
    };
  }

  if (!flowUsesTriggerTemplates(fm)) {
    return { cancelled: false, payload: {} };
  }

  const defaultFp = defaults.file_path?.trim() ?? '';
  const fpRaw = window.prompt(
    'This flow expects trigger fields from document.edited. Enter content-relative file_path (example: personal/house-sale/README.md). Cancel to abort Run now.',
    defaultFp,
  );
  if (fpRaw === null) return { cancelled: true };
  const file_path = fpRaw.trim();
  if (!file_path) {
    return { cancelled: false, payload: {} };
  }

  const uuidRaw = window.prompt(
    'Enter the backing document UUID for this file (copy from the URL or tab metadata). Used for branch_slug:',
    '',
  );
  if (uuidRaw === null) return { cancelled: true };
  const document_id = uuidRaw.trim();
  if (!UUID_REGEX.test(document_id)) {
    window.alert('A valid document UUID is required for GitSource manual runs.');
    return { cancelled: true };
  }

  const branch_slug = document_id.replace(/-/g, '').slice(0, 12);

  return {
    cancelled: false,
    payload: {
      file_path,
      branch_slug,
      document_id,
    },
  };
}
